import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext } from "./FormProvider";
import { Button, Space, Tabs, Typography } from "antd";
import { focusFirstError } from "src/common/interaction";
import { FormSection } from "./FormSection";
import { exportAllErrorFields } from "src/common/joiResolver";
import { v4 as uuidv4 } from "uuid";

const { Text } = Typography;

export const FormMultiStep = ({ forms = [], submitStep = 0, useEnabledTabs = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [dynamicSectionKey, setDynamicSectionKey] = useState(() => uuidv4());
  const [enabledTabs, setEnabledTabs] = useState(new Set([forms[0]?.key]));
  const [hasReachedSubmitStep, setHasReachedSubmitStep] = useState(false);

  const {
    formState: { errors },
    trigger,
    getValues,
    defaultValues
  } = useFormContext();
  

  // Set enabledTabs and hasReachedSubmitStep on first load
  useEffect(() => {
    const newEnabledTabs = new Set([forms[0]?.key]);
    let reachedSubmitStep = false;

    for (let index = 0; index < forms.length; index++) {
      const form = forms[index];
      const hasValueInDefault = getFormFieldNames(form).some(input => {
        return defaultValues[input] !== undefined;
      });

      if (!hasValueInDefault) {
        break
      }

      if (hasValueInDefault) {
        newEnabledTabs.add(form.key); // Enable the tab if any input has a value
      }

      // Check if the form index is greater than or equal to submitStep
      if (index >= submitStep) {
        reachedSubmitStep = true; // Set hasReachedSubmitStep to true
      }
    }

    setEnabledTabs(newEnabledTabs);
    setHasReachedSubmitStep(reachedSubmitStep);
  }, [forms, defaultValues, submitStep]);

  useEffect(() => {
    if (currentStep >= submitStep) {
      setHasReachedSubmitStep(true);
    }
  }, [currentStep, submitStep]);

  const renderDynamicSection = (form) => {
    const values = getValues(form.name);
    const items = Array.isArray(values) ? values : [{}];
  
    return items.map((value, index) => {
      const sectionKey = [index, dynamicSectionKey].join(".");
      
      return {
        key: sectionKey,
        label: `${form.sections[0].label} ${index + 1}`,
        isError: errors?.[form.name]?.[index],
        inputs: form.sections[0].inputs.map((field, fieldIndex) => {
          const inputKey = [form.name, index, field.props.name, fieldIndex, dynamicSectionKey].join(".");
          return React.cloneElement(field, {
            key: inputKey,
            name: [form.name, index, field.props.name].join("."),
          });
        }),
      };
    });
  };

  const nextFormKey = useMemo(() => {
    return (
      (forms[currentStep].sections ? forms[currentStep].sections : []).length -
        1 >
        currentSection || forms[currentStep + 1]?.key
    );
  }, [forms, currentStep, currentSection]);

  // Handle next button click
  const handleNext = useCallback(async () => {
    const form = forms[currentStep];
    const sectionCount = (
      form.name
        ? getValues(form.name)
        : form.sections
        ? form.sections
        : []
    ).length;
    const isLastSection = currentSection >= sectionCount - 1;
    let isValid
    if (form.name) {
      isValid = !isLastSection ? await trigger([form.name, currentSection].join('.'))
      : await trigger([form.name, currentSection].join('.'))  && await trigger(form.name);
    } else if (form.sections) {
      isValid = !isLastSection ? await trigger(getFormFieldNames(form.sections[currentSection]))
      : await trigger(getFormFieldNames(form.sections[currentSection]))  && await trigger(getFormFieldNames(form));
    } else {
      isValid = await trigger(getFormFieldNames(form));
    }
    
    if (isValid) {
      if (!isLastSection) {
        setCurrentSection(parseInt(currentSection) + 1);
      } else if (nextFormKey) {
        const nextForm = forms[currentStep + 1];
        if (nextForm) {
          setEnabledTabs(prev => new Set([...prev, nextForm.key]));
        }
        setCurrentStep((prev) => prev + 1);
        setCurrentSection(0);
      }
    } else {
      setTimeout(() => {
        focusFirstError(
          getFormFieldNames(form.sections && !isLastSection ? form.sections[currentSection] : form)
            .map((field) => (errors[field] ? field : null))
            .filter(Boolean)
        );
      }, 500);
    }
  }, [nextFormKey, currentSection, currentStep, forms, trigger]);

  const handleOnEdit = (targetKey, action) => {
    console.log("handleOnEdit", getValues(forms[currentStep].name));

    if (action === "add") {
      handleAddNewSection();
    } else {
      handleRemoveSection(targetKey);
    }
  };

  const handleAddNewSection = () => {
    const form = forms[currentStep];
    const currentValues = getValues(form.name);
    const values = Array.isArray(currentValues) ? currentValues : [];
    
    setCurrentSection(values.length - 1);
  };

  const handleRemoveSection = (sectionKey) => {
    const sectionIndex = parseInt(sectionKey.split(".")[0], 10);
    if (currentSection > 0 && currentSection >= sectionIndex) {
      setCurrentSection(currentSection - 1);
    }
    setDynamicSectionKey(uuidv4());
  };

  const getCurrentFieldNames = () => {
    let form = forms[currentStep];
    if (form.name) {
      const values = getValues(form.name);
      return Array.isArray(values) ? 
        `${form.name}.${currentSection}` : 
        form.name;
    }

    const fieldNames = [];
    const inputs = [...(form.sections ? 
      form.sections[currentSection].inputs : 
      form.inputs)];

    while (inputs.length > 0) {
      const input = inputs.pop();
      if (input.props.name) {
        fieldNames.push(input.props.name);
        continue;
      }
      if (input.props.inputs) {
        input.props.inputs.forEach((input) => inputs.push(input));
      }
    }

    return fieldNames.filter(Boolean);
  };

  const flattenObject = (obj, parentKey = "", result = []) => {
    for (const key in obj) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      result.push(newKey);
      if (Array.isArray(obj[key])) {
        obj[key].forEach((item, index) => {
          flattenObject(item, `${newKey}.${index}`, result);
        });
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], newKey, result);
      }
    }
    return result;
  };

  const getFormFieldNames = (form) => {
    if (form.name) {
      // const values = getValues(form.name);
      // return Array.isArray(values) ? 
      //   [`${form.name}.${currentSection}`] : 
      //   [form.name];
      return [form.name];
    }

    const fieldNames = [];
    const inputs = [...(form.sections ? 
      form.sections.flatMap((section) => section.inputs) : 
      form.inputs)];

    while (inputs.length > 0) {
      const input = inputs.pop();
      if (input.props.name) {
        fieldNames.push(input.props.name);
        continue;
      }
      if (input.props.inputs) {
        input.props.inputs.forEach((input) => inputs.push(input));
      }
    }

    return fieldNames.filter(Boolean);
  };

  const hasFormErrors = useCallback(
    (form) => {
      if (!!form.name) {
        return !!errors[form.name];
      }
      const inputs = getFormFieldNames(form) || [];

      return inputs.some((field) =>
        exportAllErrorFields(errors).some((errorField) => errorField == field)
      );
    },
    [errors]
  );

  if (forms[currentStep].sections) {
    forms[currentStep].sections.forEach((section) => {
      section.isError = hasFormErrors(section);
    });
  }

  // Add this handler for tab changes
  const handleTabChange = (key) => {
    if (useEnabledTabs && !enabledTabs.has(key)) {
      return;
    }
    const index = forms.findIndex(form => form.key === key);
    setCurrentStep(index);
    setCurrentSection(0);
  };

  if (!forms || forms.length === 0) {
    return null;
  }

  return (
    <div className="flex">
      <div className="w-full">
        <Tabs
          activeKey={forms[currentStep]?.key || forms[0]?.key}
          onChange={handleTabChange}
          tabPosition="left"
          items={forms.map((form) => ({
            key: form.key,
            label: hasFormErrors(form) ? (
              <Text type="danger">{form.label}</Text>
            ) : (
              form.label
            ),
            forceRender: !useEnabledTabs,
            disabled: useEnabledTabs && !enabledTabs.has(form.key),
            children: (
              <div className="pl-6">
                {form.sections ? (
                  <FormSection
                    name={form.name}
                    sections={
                      form.name
                        ? renderDynamicSection(form)
                        : form.sections
                    }
                    activeKey={
                      form.name
                        ? `${currentSection}.${dynamicSectionKey}`
                        : form.sections[currentSection]?.key
                    }
                    onChange={(key) => {
                      if (form.name) {
                        setCurrentSection(key.split(".")[0]);
                      } else {
                        const index = form.sections.findIndex(
                          (section) => section.key === key
                        );
                        if (index !== -1) {
                          setCurrentSection(index);
                        }
                      }
                    }}
                    onEdit={form.name ? handleOnEdit : null}
                  />
                ) : (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {form.inputs?.map((input, index) => (
                      <React.Fragment key={`input-${index}-${input.props.name || ''}`}>
                        {input}
                      </React.Fragment>
                    ))}
                  </Space>
                )}

                <Space style={{ marginTop: 24 }}>
                  {hasReachedSubmitStep && (
                    <Button type="primary" htmlType="submit">
                      Save
                    </Button>
                  )}

                  {nextFormKey && (
                    <Button type="primary" onClick={handleNext}>
                      Next
                    </Button>
                  )}
                </Space>
              </div>
            )
          }))}
        />
      </div>
    </div>
  );
};
